import * as Sentry from '@sentry/browser';

import { ErrorCode, PaymentCode } from '@biteinc/common';
import { FlashBridgeMessage, PrinterDestination } from '@biteinc/enums';

import type { GcnOrder } from '~/types/gcn_order';
import MobileAppMessageType from '~/types/mobile_app_message_type';
import { OrderFlowState } from '~/types/order_flow_state';

import type { GcnError } from './gcn_bridge_interface';
import type { GetOrderResponse } from './gcn_maitred_client';
import OrderHelper from './gcn_order_helper';

export function setOrderFlowState(state: OrderFlowState, order?: any, error?: any): void {
  gcn.handleBridgeMessage({
    updatePaymentFlowState: state,
    ...(order && { order }),
    ...(error && { error }),
  });

  if (state === OrderFlowState.OrderComplete) {
    gcn.mobileAppBridge?.send(MobileAppMessageType.OrderClosed, {
      orderId: order?._id,
    });
  }
}

export async function printGuestReceipt(
  updatedOrder: GcnOrder,
  sendKioskOrderErr?: GcnError,
): Promise<void> {
  setOrderFlowState(OrderFlowState.LocalPrinting);
  try {
    const printPayload = OrderHelper.getPrintPayload(updatedOrder, sendKioskOrderErr);
    printPayload.printJobs = printPayload.printJobs.filter((printJob) => {
      return [PrinterDestination.Receipt, PrinterDestination.Test].includes(printJob.destination);
    });

    const printResult = await gcn.bridge.sendAsync<{ error?: string }>({
      event: FlashBridgeMessage.PRINT_RECEIPT,
      printPayload,
    });

    if (printResult.error) {
      setOrderFlowState(OrderFlowState.LocalPrintingFailed);
      return;
    }

    setOrderFlowState(OrderFlowState.LocalPrintingDone);
  } catch {
    setOrderFlowState(OrderFlowState.LocalPrintingFailed);
  }
}

export function handleOrderOutOfSync(clientOrder: GcnOrder, message: string): void {
  Sentry.captureMessage(message, {
    extra: {
      order: clientOrder,
    },
  });
  setOrderFlowState(OrderFlowState.SendingOrderFailedOutOfSync);
}

export function handleFailedToGetOrder(clientOrder: GcnOrder): void {
  Sentry.captureMessage('Failed to get order from server for ensureOrderIsInSyncWithServer', {
    extra: {
      order: clientOrder,
    },
  });
  setOrderFlowState(OrderFlowState.FailedToGetOrder);
}

export async function ensureOrderIsInSyncWithServer(
  clientOrder: GcnOrder,
  serverOrder: GetOrderResponse,
): Promise<boolean> {
  if (serverOrder.order.lockExpiresAt) {
    // If the order is locked on the server, then there is some kind of operation taking place
    handleOrderOutOfSync(clientOrder, 'Order is locked.');
    return false;
  }

  if (
    JSON.stringify(serverOrder.order.appliedRewards) !==
    JSON.stringify(clientOrder.attributes.appliedRewards)
  ) {
    handleOrderOutOfSync(clientOrder, 'Order has unexpected applied rewards.');
    return false;
  }

  if (
    JSON.stringify(serverOrder.order.appliedCompCards) !==
    JSON.stringify(clientOrder.attributes.appliedCompCards)
  ) {
    handleOrderOutOfSync(clientOrder, 'Order has unexpected applied comp cards.');
    return false;
  }

  if (
    JSON.stringify(serverOrder.order.appliedLoyaltyCoupons) !==
    JSON.stringify(clientOrder.attributes.appliedLoyaltyCoupons)
  ) {
    handleOrderOutOfSync(clientOrder, 'Order has unexpected loyalty coupons.');
    return false;
  }

  if (
    JSON.stringify(serverOrder.order.appliedPosCoupons) !==
    JSON.stringify(clientOrder.attributes.appliedPosCoupons)
  ) {
    handleOrderOutOfSync(clientOrder, 'Order has unexpected POS coupons.');
    return false;
  }

  if (
    JSON.stringify(serverOrder.order.appliedBiteCoupons) !==
    JSON.stringify(clientOrder.attributes.appliedBiteCoupons)
  ) {
    handleOrderOutOfSync(clientOrder, 'Order has unexpected Bite coupons.');
    return false;
  }

  if (serverOrder.order.tipTotal !== clientOrder.attributes.tipTotal) {
    handleOrderOutOfSync(clientOrder, 'Order has unexpected tip total.');
    return false;
  }

  if (serverOrder.order.discountTotal !== clientOrder.attributes.discountTotal) {
    handleOrderOutOfSync(clientOrder, 'Order has unexpected discount total.');
    return false;
  }

  if (serverOrder.order.total !== clientOrder.attributes.total) {
    // If the totals don't match, then something has gone terribly wrong
    handleOrderOutOfSync(clientOrder, 'Order has unexpected totals.');
    return false;
  }

  return true;
}

export function shouldAbortSending(errorCode: number): boolean {
  return [
    ErrorCode.RequestKeyDataMissing,
    ErrorCode.PaymentEcommError,
    PaymentCode.PaymentCardCallForAuthorization,
    PaymentCode.PaymentCardCvvInvalid,
    PaymentCode.PaymentCardExpired,
    PaymentCode.PaymentCardNumberInvalid,
    PaymentCode.PaymentCardZipInvalid,
    PaymentCode.PaymentDeclined,
    PaymentCode.PaymentDeclinedGeneric,
    PaymentCode.PaymentDoNotHonor,
    PaymentCode.PaymentInsufficientFunds,
    PaymentCode.PaymentIntegrationInvalid,
    PaymentCode.PaymentIntegrationInvalidCredentials,
    PaymentCode.PaymentLiveCardUsedInTest,
    PaymentCode.PaymentProcessingError,
    PaymentCode.PaymentRestrictedCard,
    PaymentCode.PaymentTokenError,
    PaymentCode.PaymentTransactionNotPermitted,
    PaymentCode.PaymentUnhandledError,
    ErrorCode.OrderValidationWhenClosed,
    ErrorCode.FutureOrderValidationWhenNotEnabled,
    ErrorCode.FutureOrderValidationWhenSlotExpired,
    ErrorCode.FutureOrderValidationWhenSlotFarOut,
    ErrorCode.FutureOrderValidationWhenSlotInvalid,
    ErrorCode.FutureOrderValidationWhenLeadTimeExceedsSlot,
    ErrorCode.POSValidationItem86d,
    ErrorCode.POSValidationMod86d,
    ErrorCode.POSValidationPriceOption86d,
    ErrorCode.POSValidationRuleRequirementUnmet,
    ErrorCode.OrderValidationOrderedItemsUnavailableInMenu,
    ErrorCode.StoredValueCardDeclined,
  ].includes(errorCode);
}
